/* eslint-disable import/no-anonymous-default-export */
import React, { useState, useEffect } from 'react';
import { Icon, Button, IconButton, MenuItem, CircularProgress, Switch, FormGroup, FormControlLabel } from '@mui/material';
import { FilterContainer, FilterWrapper, SearchContainer, SwitchContainer, ButtonMargin, CenterDiv, FilterSelect, SwitchText, ButtonDropContainer, Droptext, SCORE_SEARCH_BAR } from './styles';
import { get, size } from 'global/lib/lodash';
import sendData from 'redux/actions/sendData';
import ScorecardToggle from './ScorecardToggle';
import SearchBar from 'global/components/SearchBar';
import { removeOverflowFromBody } from 'global/utils';
import useDebounce from 'global/hooks/useDebounce';
import { ButtonDrop } from '../../../global/components/Button';
import TaskBanner from './TaskBanner';
import { useUser } from '../../../global/hooks/useUser';
import { useHashView } from '../../share/useHashView';
import { lowerCaseAll, stopProp } from '../../../global/utils';
import { ScoreCopyModal } from './ScoreCopyModal';
import { Row } from '../../../global/components/row';
import Confirm from '../../../global/components/confirm';

const specialchars = ['_', '*', '/', `\\`, '[', `]`, `{`, `}`, `~`, '=', '+'];

export const TARGETOPTIONS = [
  { title: 'Active', value: 'active' },
  {
    title: 'Review Scores',
    value: 'review',
    show: (p) =>
      get(p, 'assessment.status') != 'draft' &&
      !![...get(p, 'assessment.editors', []), ...get(p, 'assessment.authors', []), ...get(p, 'assessment.viewers', []), ...get(p, 'assessment.assignees', [])].find(
        (a) => a.id == get(p, 'user.me.id')
      ) &&
      get(p, 'assessment.availableStatus', []).some((f) => 'review' === f),
  },
  // {
  //   title: 'Draft Scores',
  //   value: 'draft',
  //   show: p => !![...get(p, 'assessment.editors', []), ...get(p, 'assessment.authors', [])].find(a => a.id == get(p, 'user.me.id')) && get(p, 'assessment.availableStatus', []).some(f => 'draft' === f)
  // },
  {
    title: (p) => {
      const str = get(p, 'assessment.availableStatus', []).includes('target') ? 'Target Scores' : 'Assign Previous Targets';
      // only editors can delete previous target scores
      if (!str.toLowerCase().includes('assign') && !![...get(p, 'assessment.editors', []), ...get(p, 'assessment.authors', [])].find((a) => a.id == get(p, 'user.me.id'))) {
        return (
          <Row>
            {str}
            <IconButton style={{ marginLeft: 5 }} onClick={p.deleteScores} size="small" title="Delete Assigned Target Scores">
              <Icon>delete_forever</Icon>
            </IconButton>
          </Row>
        );
      }
      return str;
    },
    value: 'target',
    // only show "Assign" button to editors/authors, but show already assigned Target values (if available) to anyone
    // Previous Targets should always be available for editors/authors regardless of targetsEnabled toggle (PFM-2458)
    show: (p) =>
      !p.isTemplate &&
      (get(p, 'assessment.availableStatus', []).includes('target') || !![...get(p, 'assessment.editors', []), ...get(p, 'assessment.authors', [])].find((a) => a.id == get(p, 'user.me.id'))),
    onSelect: (p, opts = {}) => {
      return new Promise((res) => {
        if (p.title && !p.title.props && p.title.toLowerCase().includes('assign')) {
          const sc = get(opts.scorecards, opts.scorecardIndex || 0);

          if (sc) {
            opts &&
              opts.setScoreCopyModal((s) => ({
                ...s,
                open: true,
                title: p.title,
                assessment: get(opts, 'assessment'),
                scorecardId: sc.id,
                onClose: (c) => {
                  s.onClose();
                  res({ assigned: !!get(c, 'assigned') });
                },
              }));
          }
        } else {
          res();
        }
      });
    },
  },
  {
    title: (p) => {
      const str = get(p, 'assessment.availableStatus', []).includes('goal') ? 'New Target Scores' : 'Add New Target Scores';
      // PFM-2701: only offer the delete-all control while New Target editing is
      // enabled. When the "Update New Targets" toggle is off, existing New Targets
      // stay viewable but become non-deletable (treat as enabled unless === false).
      const targetsEnabled = get(p, 'assessment.meta.targetsEnabled') !== false;
      // anyone assigned to the assessment can delete new target scores
      if (
        targetsEnabled &&
        !str.toLowerCase().includes('add new') &&
        !![...get(p, 'assessment.editors', []), ...get(p, 'assessment.authors', []), ...get(p, 'assessment.assignee', []), ...get(p, 'assessment.viewers', [])].find(
          (a) => a.id == get(p, 'user.me.id')
        )
      ) {
        return (
          <Row>
            {str}
            <IconButton style={{ marginLeft: 5 }} onClick={(e) => p.deleteScores(e, { dest: 'new-target-scores' })} size="small" title="Delete Target Scores">
              <Icon>delete_forever</Icon>
            </IconButton>
          </Row>
        );
      }
      return str;
    },
    // title: p => get(p, 'assessment.availableStatus', []).includes('goal') ? 'New Target Scores' : 'Add New Target Scores',
    value: 'goal',
    label: 'new target',
    selText: (p) =>
      p.useDiff ? (
        <>
          Showing the difference between your scores ( <strong style={{ fontStyle: 'normal' }}>|</strong> ) and <strong>new target</strong> scores (<strong>+/-</strong>)
        </>
      ) : (
        <>
          Showing <strong>new target</strong> scores{' '}
        </>
      ),
    onSelect: async (p, opts = {}) => {
      // PFM-2547: hashed share-link viewers must not be able to mint new target
      // scores. The backend /sharehash/v1 namespace doesn't expose this PATCH;
      // guard here in addition to hiding the menu item so a stale UI / racing
      // click can't slip through to a 404 + a half-mutated assessment in cache.
      if (opts.hashViewActive) return;
      if (p.title && !p.title.props && p.title.toLowerCase().includes('add new')) {
        const aid = get(opts, 'assessment.data.id');
        const resource = opts.isTemplate ? 'assessment-templates' : 'assessments';
        await sendData({
          url: `/${resource}/${aid}/new-target-scores`,
          method: 'PATCH',
          data: {
            data: {
              type: resource,
              id: aid,
            },
          },
        });

        get(opts, 'scores.update', () => 0)();
      }
    },
    show: (p) => {
      const isLocation = get(p, 'selected.listGroup') == 'location';
      const config = get(p, `scorecards.${p?.scorecardIndex}.config`);
      // PFM-2547: hashed share-link viewers must behave as if targetsEnabled is
      // false — no "New Target Scores" / "Add New Target Scores" option in the
      // filter dropdown — regardless of the assessment's own meta flag.
      if (p.hashViewActive) return false;
      // Don't show if scoring isn't enabled for this scorecard or if status is Draft
      return (
        (get(p, 'assessment.meta.targetsEnabled') !== false || get(p, 'assessment.availableStatus', []).includes('goal')) &&
        !isLocation &&
        !(config?.hasSubScores && !config?.editScoreInfo) &&
        !(get(p, 'assessment.status', '') === 'draft')
      );
    },
  },
  {
    title: 'Previous to New Target Scores',
    value: 'targetGoal',
    selText: (p) =>
      p.useDiff ? (
        <>
          Showing the difference between <strong>previous target</strong> scores ( <strong style={{ fontStyle: 'normal' }}>|</strong> ) and <strong>new target</strong> scores (<strong>+/-</strong>)
        </>
      ) : (
        <>
          Showing <strong>new target</strong> scores{' '}
        </>
      ),
    show: (p) => size(get(p, 'assessment.availableStatus', []).filter((f) => ['goal', 'target'].includes(f))) == 2,
  },
];

export const getTargetOptions = (opts = {}) => {
  opts = {
    assessment: {},
    user: {},
    ...opts,
  };

  let options = TARGETOPTIONS.filter((f) => get(f, 'show', () => true)(opts)).map((o) => ({ ...o, title: typeof o.title == 'function' ? o.title(opts) : o.title }));

  // PFM-2547: collapse the dropdown to read-only score views in hashed sessions.
  // Drop "Add New Target Scores" / "Previous to New Target Scores" entirely so
  // there's nothing to click that would attempt a write or open the popover.
  if (opts.hashViewActive) {
    const availStatus = get(opts, 'assessment.availableStatus', []);
    options = options.filter((o) => {
      if (o.value === 'goal') return availStatus.includes('goal');
      if (o.value === 'targetGoal') return availStatus.includes('target') && availStatus.includes('goal');
      return true;
    });
  }

  if (get(opts, 'assessment.meta.targetsEnabled') === false) {
    const availStatus = get(opts, 'assessment.availableStatus', []);
    options = options.filter((o) => {
      // Previous Targets (target) should always remain available for editors/authors (PFM-2458)
      if (o.value === 'target') return true;
      if (o.value === 'goal') return availStatus.includes('goal');
      if (o.value === 'targetGoal') return availStatus.includes('target') && availStatus.includes('goal');
      return true;
    });
  }

  if (opts.printView) {
    const availableStatus = get(opts, 'assessment.availableStatus', []);
    options = options
      .filter((o) => {
        // In print view, only show score types that have existing data
        if (o.value === 'target') return availableStatus.includes('target');
        if (o.value === 'goal') return availableStatus.includes('goal');
        return true;
      })
      .map((o) => ({
        ...o,
        // Strip React elements (delete buttons) from titles, keep text only
        title: React.isValidElement(o.title) ? (Array.isArray(o.title.props.children) ? o.title.props.children[0] : o.title.props.children) : o.title,
        onSelect: undefined,
      }));
  }

  return options;
};

export default (props) => {
  const { selected, setFilter, buttons, changeSearch, searchString, loading, defaultFilters } = props;
  const [searchStringLocal, setSearchStringLocal] = useState('');

  const isDefault = (f) => !searchString && Object.keys(defaultFilters).every((k) => (Array.isArray(f[k]) ? f[k].length === 0 : defaultFilters[k] === f[k]));

  const _setFilter = (f) => {
    setFilter((fs) => ({ ...fs, ...f, isDefault: isDefault({ ...fs, ...f }) }));
  };

  const [scoreCopyModal, setScoreCopyModal] = useState({
    ...props,
    open: false,
    isShowingNone: (v) => {
      setScoreCopyModal((s) => ({ ...s, showedNone: v }));
    },
    onClose: () => {
      setScoreCopyModal((s) => {
        if (s.showedNone) {
          _setFilter({ assessment_status: 'active' });
        }
        return { ...s, open: !s.open };
      });
    },
  });
  const [state, setState] = useState({ isLoadingSelection: false });
  const [confirm, setConfirm] = useState({});
  const user = useUser();
  const { active: hashViewActive } = useHashView();

  const _searchStringDebounced = useDebounce(searchStringLocal, 700);
  const searchStringDebounced = size(_searchStringDebounced) >= 3 || specialchars.find((f) => _searchStringDebounced.indexOf(f) > -1) ? _searchStringDebounced : null;

  useEffect(() => {
    if (searchString !== searchStringLocal) {
      if (!searchString) {
        setSearchStringLocal('');
      } else {
        setSearchStringLocal(searchString);
      }
    }
  }, [searchString]);

  useEffect(() => {
    if (searchString !== searchStringLocal) {
      (async () => {
        try {
          // Promise.race between handleAskWallter and a 1s timeout
          await Promise.race([handleAskWallter(), new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 1000))]);
        } catch (e) {
          if (e.message === 'Timeout') {
            console.warn('handleAskWallter took too long, moving on');
          } else {
            console.error(e);
          }
        } finally {
          changeSearch(searchStringDebounced);
        }
      })();
    }
  }, [searchStringDebounced]);

  const handleAskWallter = async () => {
    const scorecardIndex = get(props, 'scorecardIndex');
    const scorecardId = get(props, `scorecards.${scorecardIndex}.id`);
    if (!searchStringDebounced || !scorecardId) return;
    const wallterResponse = await sendData({
      url: `/wallter?scorecard_id=${scorecardId}&query=${searchStringDebounced}`,
    });
    const responseData = wallterResponse.data || {};

    // filter out the request id key
    const items = Object.values(responseData).filter((item) => typeof item === 'object' && item.checkpoint_id);
    const checkpointIds = items.map((item) => item.checkpoint_id || item.checkpointId).filter(Boolean);

    setFilter((f) => ({ ...f, suggestedCpIds: checkpointIds }));
  };

  const selVal = get(selected, 'value');
  const loadingIcon = loading || (searchString && searchString !== searchStringLocal);

  const updateScoresAfterCopy = () => {
    get(props, 'scores.update', () => 0)();
  };

  const deleteScores = (e, opts = {}) => {
    stopProp(e);

    opts = {
      dest: 'target-scores',
      ...opts,
    };

    // PFM-2701: defensively block deleting New Targets when the "Update New Targets"
    // toggle is off (treat as enabled unless === false). The delete-all button is
    // already hidden in that case; this guards against a stale UI / racing click.
    if (opts.dest === 'new-target-scores' && get(props, 'assessment.data.meta.targetsEnabled') === false) {
      return;
    }

    const aid = get(props, 'assessment.data.id');
    const resource = props.isTemplate ? 'assessment-templates' : 'assessments';
    setConfirm({
      title: 'Delete Target Scores',
      content: 'Are you sure you want to delete the Target Scores from this Assessment?',
      onOk: async () => {
        try {
          await sendData({
            url: `/${resource}/${aid}/${opts.dest}`,
            method: 'DELETE',
          });

          setConfirm({});
          updateScoresAfterCopy();
        } catch (err) {
          alert(err.toString());
        }
      },
      onCancel: () => {
        setConfirm({});
      },
    });
  };

  const renderButtonDrop = () => {
    const options = getTargetOptions({ ...props, user, assessment: props.assessment.data, deleteScores, hashViewActive });

    const sel = options.find((o) => o.value === selected.assessment_status) || {};

    const toggleDiff = () => {
      setFilter((f) => ({ ...f, useDiff: !f.useDiff }));
    };

    return size(options) > 1
      ? {
          sel,
          text: (
            <Droptext>
              {sel.value !== 'active' &&
                (selected.useDiff ? (
                  <span>
                    {sel.selText ? (
                      sel.selText({ ...selected })
                    ) : (
                      <>
                        Showing the difference between <strong>{lowerCaseAll(sel?.label ?? sel.value)}</strong> scores ( <strong style={{ fontStyle: 'normal' }}>|</strong> ) and your scores (
                        <strong>+/-</strong>)
                      </>
                    )}
                    <span className="difflink" onClick={toggleDiff}>{`[show score]`}</span>
                  </span>
                ) : (
                  <span>
                    {sel.selText ? (
                      sel.selText({ ...selected })
                    ) : (
                      <>
                        Showing <strong>{lowerCaseAll(sel?.label ?? sel.value)}</strong> scores{' '}
                      </>
                    )}
                    <span className="difflink" onClick={toggleDiff}>{`[show diff]`}</span>
                  </span>
                ))}
            </Droptext>
          ),
          comp: (
            <ButtonDropContainer>
              <ButtonDrop
                onSelect={async (o) => {
                  setState((s) => ({ ...s, isLoadingSelection: true }));
                  const result = o.onSelect && (await o.onSelect(o, { ...props, setScoreCopyModal, hashViewActive }));
                  // PFM-2566: When the user opens "Assign Previous Targets" but no targets get
                  // assigned (empty state, or modal dismissed without selecting), the modal's
                  // onClose resolves with { assigned: false }. Don't switch to 'target' view in
                  // that case — there is no target data and the score render path will WSOD.
                  if (get(result, 'assigned') !== false) {
                    setFilter((f) => ({ ...f, assessment_status: o.value, useDiff: ['goal', 'target'].includes(o.value) ? true : f.useDiff }));
                  }
                  setState((s) => ({ ...s, isLoadingSelection: false }));
                }}
                variant="text"
                color="secondary"
                icon="list_alt"
                data-testid="assessment-score-mode-menu-button"
                options={options.map((o) => ({ ...o, selected: o.value == selected.assessment_status }))}
              >
                {state.isLoadingSelection ? <CircularProgress size={16} style={{ width: 16, height: 16 }} /> : <Icon className="innericon">arrow_drop_down</Icon>}
              </ButtonDrop>
            </ButtonDropContainer>
          ),
        }
      : { comp: undefined, text: undefined };
  };

  const bdrop = renderButtonDrop();

  return (
    <>
      <FilterContainer>
        {!props.printView && <ScorecardToggle {...props} />}
        <FilterWrapper withBorder>
          {!props.printView && <TaskBanner {...props.taskBanner} />}
          {(!get(props, 'taskBanner.showSelectBoxes') || props.isTemplate) && (
            <FilterWrapper>
              <SearchBar
                onClear={() => {
                  setSearchStringLocal('');
                }}
                SearchIcon={
                  loadingIcon ? (
                    <CircularProgress size={24} style={{ width: 24, height: 24, marginLeft: 12, marginRight: 12 }} />
                  ) : isDefault(selected) ? (
                    <Icon style={{ color: 'grey', width: 48, height: 48, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>search</Icon>
                  ) : (
                    <IconButton
                      onClick={() => {
                        setFilter(defaultFilters);
                        changeSearch('');
                      }}
                      size="large"
                    >
                      <Icon>replay</Icon>
                    </IconButton>
                  )
                }
                changeSearch={setSearchStringLocal}
                searchString={searchStringLocal}
                containerStyle={SCORE_SEARCH_BAR}
                disableInputPaddingLeft
              />
              {bdrop.comp}
              {buttons.map((button, i) => {
                const show = get(button, 'show', () => true)(props);
                if (!show) {
                  return null;
                }
                const type = get(button, 'type', 'default');
                if (type === 'select') {
                  const selectSelected = button.options.find((o) => o.val === get(selected, button.val));
                  const active = get(selectSelected, 'val') !== get(defaultFilters, get(button, 'val'));
                  return (
                    <FilterSelect
                      key={button.val}
                      value={get(selectSelected, 'val')}
                      variant="filled"
                      defaultValue="N/A"
                      isSelected={!!active}
                      disableUnderline
                      onChange={(e) => {
                        _setFilter({ [button.val]: e.target.value });
                        button.onChange && button.onChange(e.target.value);
                      }}
                      onClose={() => removeOverflowFromBody()}
                    >
                      {button.options.map((opt, i) => {
                        return (
                          <MenuItem key={i} value={get(opt, 'val')}>
                            {opt.text}
                          </MenuItem>
                        );
                      })}
                    </FilterSelect>
                  );
                } else if (type === 'switch') {
                  const on = !!get(props, button.val);
                  return (
                    <SwitchContainer key={button.val}>
                      <FormGroup row>
                        <CenterDiv
                          as={FormControlLabel}
                          control={
                            <Switch
                              checked={on}
                              onChange={(e) => button.onChange(e.target.checked)}
                              inputProps={{ 'aria-label': 'secondary checkbox' }}
                              disabled={button.disabled ? button.disabled(props) : false}
                            />
                          }
                          label={get(button, ['text', on ? 'on' : 'off'], get(button, 'text'))}
                        />
                      </FormGroup>
                    </SwitchContainer>
                  );
                }

                const isSelected = selVal === get(button, 'val');
                return (
                  <ButtonMargin as={Button} key={i} size={'small'} onClick={() => _setFilter({ value: button.val })} variant="contained" color={isSelected ? 'primary' : undefined} disableElevation>
                    {button.text}
                  </ButtonMargin>
                );
              })}
            </FilterWrapper>
          )}
        </FilterWrapper>
        {bdrop.text}
        {!props.printView && <ScoreCopyModal {...scoreCopyModal} updateScoresAfterCopy={updateScoresAfterCopy} />}
        {!props.printView && <Confirm {...confirm} />}
      </FilterContainer>
    </>
  );
};
